import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { PERSON_FUNCTIONS, find } from "@/lib/constants";
import { PeopleMailDialog } from "./PeopleMailDialog";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Mail, Search, UsersRound } from "lucide-react";

function FnBadges({ functions }) {
  return (
    <div className="flex flex-wrap gap-1">
      {(functions || []).map((fn) => {
        const f = find(PERSON_FUNCTIONS, fn);
        return <span key={fn} className={`text-[11px] font-medium px-2 py-0.5 rounded border ${f.cls}`}>{f.label}</span>;
      })}
    </div>
  );
}

export default function PeopleSettings() {
  const [people, setPeople] = useState([]);
  const [q, setQ] = useState("");
  const [fn, setFn] = useState("alle");
  const [selected, setSelected] = useState([]);
  const [mail, setMail] = useState({ open: false, recipients: [] });
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try { const { data } = await api.get("/people"); setPeople(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = people.filter((p) => (fn === "alle" || (p.functions || []).includes(fn))
    && (!q || `${p.name} ${p.email} ${p.phone}`.toLowerCase().includes(q.toLowerCase())));
  const allSel = filtered.length > 0 && filtered.every((p) => selected.includes(p.id));
  const toggleAll = () => setSelected(allSel ? selected.filter((id) => !filtered.some((p) => p.id === id)) : [...new Set([...selected, ...filtered.map((p) => p.id)])]);
  const toggleOne = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const openPerson = (id) => navigate(`/einstellungen/mitwirkende/${id}`, { state: { ids: filtered.map((x) => x.id) } });

  const remove = async (p) => {
    if (!window.confirm(`${p.name} wirklich löschen?`)) return;
    try { await api.delete(`/people/${p.id}`); setSelected((s) => s.filter((x) => x !== p.id)); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openMail = (list) => setMail({ open: true, recipients: list });
  const { user } = useAuth();
  const chef = !!user?.is_chef;

  return (
    <div className="max-w-5xl">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center text-amber-600"><UsersRound size={22} /></div>
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Mitwirkende</h1>
            <p className="text-slate-500 text-sm">Redaktion, Vorstand, Autoren, Fotografen &amp; Co. – für Artikel-Zuordnung und Mailversand.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" disabled={!selected.length || !chef} title={chef ? "" : "Mailversand nur für den Chefredakteur"} onClick={() => openMail(people.filter((p) => selected.includes(p.id)))} data-testid="people-mail-selected">
            <Mail size={15} className="mr-1.5" /> E-Mail an Auswahl ({selected.length})
          </Button>
          <Button onClick={() => navigate("/einstellungen/mitwirkende/neu")} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="add-person-button">
            <Plus size={16} className="mr-1.5" /> Person hinzufügen
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex flex-wrap gap-1 bg-slate-100 p-1 rounded-lg">
          {[{ value: "alle", label: "Alle" }, ...PERSON_FUNCTIONS].map((f) => (
            <button key={f.value} onClick={() => setFn(f.value)} data-testid={`people-filter-${f.value}`}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${fn === f.value ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen…" className="pl-9 w-56" data-testid="people-search" />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto shadow-sm">
        <table className="w-full text-sm" data-testid="people-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 w-10"><Checkbox checked={allSel} onCheckedChange={toggleAll} data-testid="people-select-all" /></th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Funktionen</th>
              <th className="text-left px-4 py-3">Kontakt</th>
              <th className="text-right px-4 py-3">Aktionen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((p) => (
              <tr key={p.id} onClick={() => openPerson(p.id)} className="hover:bg-slate-50 cursor-pointer" data-testid={`person-row-${p.id}`}>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.includes(p.id)} onCheckedChange={() => toggleOne(p.id)} data-testid={`person-select-${p.id}`} /></td>
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-900">{p.name}</div>
                  {p.notes && <div className="text-xs text-slate-400 line-clamp-1">{p.notes}</div>}
                </td>
                <td className="px-4 py-3"><FnBadges functions={p.functions} /></td>
                <td className="px-4 py-3 text-slate-600">
                  <div>{p.email || "–"}</div>
                  <div className="text-xs text-slate-400">{p.phone}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button size="icon" variant="ghost" disabled={!p.email || !chef} onClick={() => openMail([p])} title={chef ? "E-Mail senden" : "Mailversand nur für den Chefredakteur"} data-testid={`person-mail-${p.id}`}><Mail size={16} className="text-blue-600" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => openPerson(p.id)} data-testid={`person-edit-${p.id}`}><Pencil size={16} className="text-slate-500" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(p)} data-testid={`person-delete-${p.id}`}><Trash2 size={16} className="text-red-500" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">Keine Personen gefunden.</td></tr>}
          </tbody>
        </table>
      </div>

      <PeopleMailDialog open={mail.open} onOpenChange={(o) => setMail((m) => ({ ...m, open: o }))} recipients={mail.recipients} />
    </div>
  );
}
